import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { aiSkillsAdminApi } from '@/api/aiSkillsAdmin';
import { appConfigApi } from '@/api/appConfig';
import { Card } from '@/components/ui/Card';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import {
  AI_SKILL_KEYS,
  aiSkillConfigKey,
  aiSkillServerOn,
  canSeeServerCard,
  journalOf,
  listeningEmpty,
  skillCounts,
  type AiSkillKey,
} from '@/lib/aiSkillsAdmin';
import { humanizeError } from '@/lib/errors';
import { useAuthStore } from '@/stores/auth';

import { journalLine, toggleConfirm } from './aiSkillsServerText';

const OVERVIEW_KEY = ['admin', 'ai-skills-overview'];

/**
 * Тумблеры ИИ-заданий по навыкам — уровень сервера (docs/CONTRACTS/ai-skills.md §4).
 * Выключенный здесь навык пропадает у всех учеников; курс может выключить
 * ещё и у своих (карточка курса, `marathon/AiSkillsCard.tsx`).
 *
 * Заход 2 (16.10.2026): под каждым навыком — сколько учеников увидят его сразу и
 * сколько после mock test (`GET /admin/ielts/ai-skills/overview`), переключение —
 * только через подтверждение с этими числами, внизу журнал «кто, когда».
 *
 * Тот же запрос, что у страницы флагов (`['admin','app-config']`): ответ на
 * правку — документ целиком, его и кладём, как там.
 */
export function AiSkillsServerCard() {
  const role = useAuthStore((s) => s.user?.role);
  // Manager страницу флагов видит, но правка и сводка ему — 403.
  if (!canSeeServerCard(role)) return null;
  return <ServerCardBody />;
}

function ServerCardBody() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const ask = useConfirm();
  const config = useQuery({ queryKey: ['admin', 'app-config'], queryFn: appConfigApi.get });
  const overview = useQuery({ queryKey: OVERVIEW_KEY, queryFn: aiSkillsAdminApi.overview });
  const toggle = useMutation({
    mutationFn: ({ skill, next }: { skill: AiSkillKey; next: boolean }) => appConfigApi.set(aiSkillConfigKey(skill), next),
    onSuccess: async (doc) => {
      qc.setQueryData(['admin', 'app-config'], doc);
      await qc.invalidateQueries({ queryKey: OVERVIEW_KEY });
    },
  });

  const onToggle = async (skill: AiSkillKey, nextOn: boolean) => {
    const c = toggleConfirm(t, skill, nextOn, overview.data);
    const ok = await ask({ title: t(`adminAiSkills.skill.${skill}`), message: c.message, confirmLabel: c.confirmLabel, danger: c.danger });
    if (ok) toggle.mutate({ skill, next: nextOn });
  };

  const formatAt = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const o = overview.data;
  const journal = journalOf(o);

  return (
    <Card>
      <h2 className="mb-1 font-display text-base font-bold text-on-surface">{t('adminAiSkills.serverTitle')}</h2>
      <p className="mb-2 text-sm text-on-surface-variant">{t('adminAiSkills.serverNote')}</p>
      {o && (
        <p className="mb-3 text-sm text-on-surface">
          {t('adminAiSkills.windowsLine', { count: o.mock_windows.assigned, open: o.mock_windows.open_now })}
        </p>
      )}
      <ul className="divide-y divide-outline-variant">
        {AI_SKILL_KEYS.map((skill) => {
          const on = aiSkillServerOn(config.data, skill);
          const busy = toggle.isPending && toggle.variables?.skill === skill;
          const counts = skillCounts(o, skill);
          return (
            <li key={skill} className="flex items-start justify-between gap-4 py-3">
              <div className="min-w-0">
                <span className="text-sm text-on-surface">{t(`adminAiSkills.skill.${skill}`)}</span>
                {counts && (
                  <p className="text-xs text-on-surface-variant">
                    {t('adminAiSkills.countsLine', { now: counts.now, after: counts.after })}
                  </p>
                )}
                {skill === 'listening' && o && (
                  <p className={listeningEmpty(o, skill) ? 'text-xs text-error' : 'text-xs text-on-surface-variant'}>
                    {listeningEmpty(o, skill)
                      ? t('adminAiSkills.listeningNone')
                      : t('adminAiSkills.listeningLine', { ready: o.listening.ready, approved: o.listening.approved })}
                  </p>
                )}
              </div>
              <AiSkillSwitch
                on={on}
                label={t(`adminAiSkills.skill.${skill}`)}
                disabled={busy || config.isLoading}
                onToggle={() => void onToggle(skill, !on)}
              />
            </li>
          );
        })}
      </ul>
      {overview.isError && (
        <p className="mt-3 text-xs text-on-surface-variant">
          {t('adminAiSkills.overviewError')} {humanizeError(overview.error)}
        </p>
      )}
      {(config.isError || toggle.isError) && (
        <p className="mt-3 text-sm text-error">{humanizeError(config.error ?? toggle.error)}</p>
      )}

      {o && (
        <div className="mt-4 border-t border-outline-variant pt-3">
          <h3 className="mb-2 text-sm font-semibold text-on-surface">{t('adminAiSkills.journalTitle')}</h3>
          {journal.length === 0 ? (
            <p className="text-xs text-on-surface-variant">{t('adminAiSkills.journalEmpty')}</p>
          ) : (
            <ul className="max-h-56 space-y-1 overflow-y-auto text-xs text-on-surface-variant">
              {journal.map((e, i) => (
                <li key={`${e.at}-${i}`} className="break-words">
                  {journalLine(t, e, formatAt)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

/** Переключатель той же формы, что на странице флагов. */
export function AiSkillSwitch({
  on,
  label,
  disabled,
  dimmed,
  onToggle,
}: {
  on: boolean;
  label: string;
  disabled?: boolean;
  /** Серый: включено здесь, но выключено уровнем выше (сервером). */
  dimmed?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      className={[
        'relative h-6 w-11 flex-none rounded-full transition-colors',
        on && !dimmed ? 'bg-primary' : 'bg-outline-variant',
        disabled ? 'opacity-50' : '',
      ].join(' ')}
    >
      <span
        className={[
          'absolute top-0.5 h-5 w-5 rounded-full bg-surface transition-all',
          on ? 'left-[calc(100%-1.375rem)]' : 'left-0.5',
        ].join(' ')}
      />
    </button>
  );
}
